import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCompileStatusBadge } from '../../utils/compileStatus';
import useContextualPopover from '../../hooks/useContextualPopover';
import ContextualPopover from '../../components/ContextualPopover/ContextualPopover';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Card,
  CardContent,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Download as ExportIcon,
  Visibility as ViewIcon,
  PlayArrow as CompileIcon,
  Settings as ConfigureIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  fetchEvents,
  deleteEvent,
  compileEvent,
  setFilters,
  clearSuccessMessage
} from './customEventsSlice';
import { CardSkeleton, TableSkeleton } from '../../components/LoadingSpinner';
import { EvaluationStatusBanner } from '../evaluations';
import DWLFLoader from '../../components/DWLFLoader';
import EmptyState from '../../components/common/EmptyState';
import { getMyQuotas } from '../../api/quotas';
import { CustomEventMetadataChips } from './customEventMetadata';

const CustomEventsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    events,
    eventsLoading,
    eventsError,
    filters,
    isDeleting,
    isCompiling,
    successMessage
  } = useSelector((state) => state.customEvents);

  const [localFilters, setLocalFilters] = useState(filters);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [currentPlanTier, setCurrentPlanTier] = useState(null);

  // Contextual popover for first visit
  const customEventsPopover = useContextualPopover('first-custom-events-visit');
  const addButtonRef = useRef(null);

  // Check if any filters are active
  const hasActiveFilters = filters.complexity || (filters.tags && filters.tags.length > 0);

  // Fetch events when filters change
  useEffect(() => {
    dispatch(fetchEvents(filters));
  }, [dispatch, filters]);

  // Fetch quota information once on mount
  useEffect(() => {
    getMyQuotas().then(data => {
      const eventsQuota = data.quotas?.customEvents;
      if (eventsQuota) {
        setQuotaInfo(eventsQuota);
      }
      setCurrentPlanTier(data.tier || null);
    }).catch(err => {
      console.error('Failed to fetch quotas:', err);
    });
  }, [dispatch]);

  // Show contextual popover on first visit
  useEffect(() => {
    // Destructure to avoid unstable object reference as dependency
    const { show: popoverShow, setAnchorEl: setPopoverAnchor } = customEventsPopover;
    if (!eventsLoading && addButtonRef.current && !popoverShow) {
      const timer = setTimeout(() => {
        setPopoverAnchor(addButtonRef.current);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [eventsLoading, customEventsPopover.show, customEventsPopover.setAnchorEl]);

  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      setTimeout(() => {
        dispatch(clearSuccessMessage());
        setSnackbarOpen(false);
      }, 4000);
    }
  }, [successMessage, dispatch]);

  const handleFilterChange = (field, value) => {
    setLocalFilters({ ...localFilters, [field]: value });
  };

  const handleApplyFilters = () => {
    const appliedFilters = { ...localFilters, offset: 0 };
    dispatch(setFilters(appliedFilters));
    dispatch(fetchEvents(appliedFilters));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      limit: 50,
      offset: 0,
      tags: [],
      complexity: '',
      timeframe: ''
    };
    setLocalFilters(clearedFilters);
    dispatch(setFilters(clearedFilters));
    dispatch(fetchEvents(clearedFilters));
  };

  const handlePageChange = (event, newPage) => {
    const newOffset = newPage * filters.limit;
    const newFilters = { ...filters, offset: newOffset };
    dispatch(setFilters(newFilters));
    dispatch(fetchEvents(newFilters));
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    const newFilters = { ...filters, limit: newLimit, offset: 0 };
    dispatch(setFilters(newFilters));
    dispatch(fetchEvents(newFilters));
  };

  const handleCreateNew = () => {
    navigate('/custom-events/new');
  };

  const handleEdit = (eventId) => {
    navigate(`/custom-events/${eventId}/edit`);
  };

  const handleView = (eventId) => {
    navigate(`/custom-events/${eventId}/view`);
  };

  const handleDeleteClick = (customEvent) => {
    setEventToDelete(customEvent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (eventToDelete) {
      dispatch(deleteEvent(eventToDelete.eventId));
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  const handleDuplicate = (customEvent) => {
    navigate('/custom-events/new', {
      state: {
        duplicateFrom: customEvent,
        name: `${customEvent.name} (Copy)`
      }
    });
  };

  const handleExport = (customEvent) => {
    const dataStr = JSON.stringify(customEvent, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `${customEvent.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_event.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleConfigure = (eventId) => {
    navigate(`/custom-events/${eventId}/configure`);
  };

  const handleCompile = (eventId) => {
    dispatch(compileEvent(eventId));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
    case 'simple': return 'success';
    case 'moderate': return 'warning';
    case 'complex': return 'error';
    default: return 'default';
    }
  };

  // Use shared utility for compile status badge
  const getCompileStatus = (customEvent) => {
    const label = getCompileStatusBadge(customEvent);
    if (!label) return null;
    return { label, color: 'warning' };
  };

  if (eventsLoading && !events.length) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="300px" height={20} />
          </Box>
          <Skeleton variant="rectangular" width="120px" height={40} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 3 }} />
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton variant="text" width="100px" height={24} sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 1fr auto auto' }, gap: 2 }}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" width="80px" height={40} />
            <Skeleton variant="rectangular" width="80px" height={40} />
          </Box>
        </Paper>
        <TableSkeleton rows={5} columns={6} />
      </Container>
    );
  }

  if (eventsError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4, borderRadius: 0 }}>
          {eventsError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography 
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: { xs: '1.75rem', md: '2rem' },
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: 'var(--dwlf-text)',
              mb: 0.5,
            }}
          >
            Custom Events
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.85rem',
                color: 'var(--dwlf-ink-50)',
              }}
            >
              Build reusable market behavior detectors
            </Typography>
            {quotaInfo && (
              <Chip
                label={`${quotaInfo.used ?? events.length}/${quotaInfo.limit ?? '∞'} events used`}
                size="small"
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.75rem',
                  backgroundColor: (quotaInfo.used ?? events.length) >= (quotaInfo.limit ?? Infinity) 
                    ? 'rgba(255, 87, 34, 0.2)' 
                    : 'var(--dwlf-accent-20)',
                  color: (quotaInfo.used ?? events.length) >= (quotaInfo.limit ?? Infinity) 
                    ? '#ff5722' 
                    : 'var(--dwlf-accent)',
                  borderColor: (quotaInfo.used ?? events.length) >= (quotaInfo.limit ?? Infinity) 
                    ? '#ff5722' 
                    : 'var(--dwlf-accent)',
                  border: '1px solid',
                }}
              />
            )}
          </Box>
        </Box>
        <Button
          ref={addButtonRef}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          disabled={quotaInfo && (quotaInfo.used ?? events.length) >= (quotaInfo.limit ?? Infinity)}
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 0,
            backgroundColor: 'var(--dwlf-accent)',
            color: 'var(--dwlf-bg)',
            px: 2.5,
            py: 1,
            '&:hover': {
              backgroundColor: 'var(--dwlf-text)',
            },
            '&:disabled': {
              backgroundColor: 'var(--dwlf-ink-12)',
              color: 'var(--dwlf-ink-30)',
            },
          }}
        >
          New Event
        </Button>
      </Box>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          sx={{
            mb: 3,
            borderColor: 'var(--dwlf-accent)',
            backgroundColor: 'var(--dwlf-accent-05)',
          }}
        >
          <CardContent>
            <Typography 
              sx={{ 
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'var(--dwlf-accent)',
                mb: 1,
              }}
            >
              Build reusable market behaviors
            </Typography>
            <Typography 
              sx={{ 
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.8rem',
                color: 'var(--dwlf-ink-60)',
              }}
            >
              Custom events compose indicator signals into higher-level market meaning
            </Typography>
          </CardContent>
        </Card>
      </motion.div>

      <EvaluationStatusBanner type="events" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography 
            sx={{ 
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: 'var(--dwlf-ink-70)',
              mb: 2,
            }}
          >
            Filters
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '200px 1fr auto auto' },
            gap: 2,
            alignItems: 'end',
          }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem' }}>Complexity</InputLabel>
              <Select
                value={localFilters.complexity}
                label="Complexity"
                onChange={(e) => handleFilterChange('complexity', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="simple">Simple</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="complex">Complex</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Tags (comma-separated)"
              value={localFilters.tags.join(', ')}
              onChange={(e) =>
                handleFilterChange('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))
              }
              InputLabelProps={{
                sx: { fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8rem' }
              }}
            />
            <Button 
              variant="contained" 
              onClick={handleApplyFilters}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                backgroundColor: 'var(--dwlf-accent)',
                color: 'var(--dwlf-bg)',
                px: 2,
                minWidth: 'auto',
                '&:hover': { backgroundColor: 'var(--dwlf-text)' },
              }}
            >
              Apply
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleClearFilters}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                borderColor: 'var(--dwlf-ink-20)',
                color: 'var(--dwlf-ink-60)',
                px: 2,
                minWidth: 'auto',
                '&:hover': {
                  borderColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-accent)',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Clear
            </Button>
          </Box>
        </Paper>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {events.length === 0 ? (
          hasActiveFilters ? (
            <EmptyState
              icon={<EventIcon sx={{ fontSize: 80, color: 'var(--dwlf-accent)' }} />}
              title="No events found"
              subtitle="No custom events match the selected filters. Try adjusting your filter criteria or create a new event."
              buttonLabel="Clear Filters"
              onAction={handleClearFilters}
            />
          ) : (
            <EmptyState
              icon={<EventIcon sx={{ fontSize: 80, color: 'var(--dwlf-accent)' }} />}
              title="No custom events yet"
              subtitle="Create your first custom event to start detecting market behaviors. Events combine indicator signals into higher-level market meaning."
              buttonLabel="Create First Event"
              onAction={handleCreateNew}
              showAddIcon
            />
          )
        ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="30px"></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Complexity</TableCell>
                <TableCell>Nodes</TableCell>
                <TableCell>Last Modified</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((customEvent) => {
                const compileStatus = getCompileStatus(customEvent);
                // Generate a stable color based on event name hash
                const nameHash = customEvent.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                const colorHue = (nameHash * 137.5) % 360;
                const eventColor = `hsl(${colorHue}, 70%, 60%)`;
                return (
                <TableRow key={customEvent.eventId} hover>
                  <TableCell>
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: eventColor,
                        boxShadow: `0 0 8px ${eventColor}`,
                      }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {customEvent.name}
                      </Typography>
                      {compileStatus && (
                        <Chip
                          label={compileStatus.label}
                          color={compileStatus.color}
                          size="small"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: '20px'
                          }}
                        />
                      )}
                    </Box>
                    <CustomEventMetadataChips
                      customEvent={customEvent}
                      currentPlanTier={currentPlanTier}
                      sx={{ mt: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {customEvent.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={customEvent.stats?.complexity || 'Unknown'}
                      color={getComplexityColor(customEvent.stats?.complexity)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {customEvent.stats?.totalNodes || 0} nodes, {customEvent.stats?.totalConnections || 0} connections
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(customEvent.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleView(customEvent.eventId)}
                        title="View"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(customEvent.eventId)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(customEvent)}
                        title="Duplicate"
                      >
                        <DuplicateIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleCompile(customEvent.eventId)}
                        disabled={isCompiling}
                        title="Compile Event"
                      >
                        <CompileIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleConfigure(customEvent.eventId)}
                        title="Configure Symbols"
                        color="primary"
                      >
                        <ConfigureIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleExport(customEvent)}
                        title="Export JSON"
                      >
                        <ExportIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(customEvent)}
                        disabled={isDeleting}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={-1}
            page={Math.floor(filters.offset / filters.limit)}
            onPageChange={handlePageChange}
            rowsPerPage={filters.limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
        )}
      </motion.div>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Custom Event</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{eventToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 600,
            fontSize: '1rem',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-message': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
          onClose={() => setSnackbarOpen(false)}
        >
          <span style={{ color: 'white' }}>
            ✅ {successMessage}
          </span>
        </Alert>
      </Snackbar>

      <ContextualPopover
        open={customEventsPopover.show}
        anchorEl={customEventsPopover.anchorEl}
        onDismiss={customEventsPopover.dismiss}
        message="Click here to start building your first custom event detector."
      />
    </Container>
  );
};

export default CustomEventsPage;
